import { DateInterval, ProfessionalAvailability } from '../../models';
import { CalendarSchedule } from '../../components/CustomDayCalendar/CustomDayCalendar';
import { Guid, getRecurringDates, parseTimeStringToMinutes } from '../utils';

export interface AvailabilityProfessional {
    id: string
    colorCode?: string
    timeAvailability?: ProfessionalAvailability[]
    absences?: DateInterval[]
    availabilityExtra?: DateInterval[]
    branchTimeAvailability?: Record<string, ProfessionalAvailability[]>
    branchAbsences?: Record<string, DateInterval[]>
    branchAvailabilityExtra?: Record<string, DateInterval[]>
}

const expandRecurring = (items: DateInterval[], rangeTo: string): DateInterval[] =>
    items.flatMap(a => {
        if (!a.recurring) return [a];
        const diffHours = (new Date(a.to).getTime() - new Date(a.from).getTime()) / (1000 * 60 * 60);
        return getRecurringDates(a.recurring, a.from, a.from, rangeTo)
            .map(d => ({ ...a, from: d.format('YYYY-MM-DDTHH:mm'), to: d.clone().add(diffHours, 'hours').format('YYYY-MM-DDTHH:mm') }));
    });

const filterForDate = (items: DateInterval[], date: Date): DateInterval[] =>
    items.filter(a => date >= new Date(a.from.substring(0, 10)) && date <= new Date(a.to.substring(0, 10)));

export const getProfessionalAvailabilitySchedules = (
    p: AvailabilityProfessional,
    date: Date,
    dateString: string,
    columnName: string,
    selectedBranch: string | undefined,
    rangeTo: string,
    absenceLabel: string,
    toWeekDay: (date: Date) => number,
    freeSlotExtras?: Partial<CalendarSchedule>,
    onAbsenceClick?: (originalAbsence: DateInterval) => void,
): CalendarSchedule[] => {
    const rawAbsences = (selectedBranch ? p.branchAbsences?.[selectedBranch] : p.absences) ?? [];

    // Expand recurring entries while keeping the original (pre-expansion) reference so edit/delete targets the right entry
    const absencesWithOriginals = rawAbsences
        .flatMap((original): Array<{ expanded: DateInterval; original: DateInterval }> => {
            if (!original.recurring) return [{ expanded: original, original }];
            const diffHours = (new Date(original.to).getTime() - new Date(original.from).getTime()) / (1000 * 60 * 60);
            return getRecurringDates(original.recurring, original.from, original.from, rangeTo)
                .map(d => ({
                    expanded: { ...original, from: d.format('YYYY-MM-DDTHH:mm'), to: d.clone().add(diffHours, 'hours').format('YYYY-MM-DDTHH:mm') },
                    original,
                }));
        })
        .filter(({ expanded }) => date >= new Date(expanded.from.substring(0, 10)) && date <= new Date(expanded.to.substring(0, 10)));

    const availabilityExtra = filterForDate(
        expandRecurring((selectedBranch ? p.branchAvailabilityExtra?.[selectedBranch] : p.availabilityExtra) ?? [], rangeTo),
        date,
    );

    const timeSlots = (selectedBranch ? p.branchTimeAvailability?.[selectedBranch] : p.timeAvailability)
        ?.find(a => new Date(a.from) <= date)?.timeSlots ?? [];

    const freeSlots = timeSlots
        .filter(a => a.dayOfWeek === toWeekDay(date))
        .map<CalendarSchedule>(a => ({
            id: Guid.newGuid(), columnName, content: '', color: p.colorCode, opacity: 0.15, ignoreOverlap: true,
            startMinutesOfDay: parseTimeStringToMinutes(a.start),
            endMinutesOfDay: parseTimeStringToMinutes(a.end),
            ...freeSlotExtras,
        }));

    const freeSlotsExtra = availabilityExtra.map<CalendarSchedule>(a => ({
        id: Guid.newGuid(), columnName, content: '', color: p.colorCode, opacity: 0.15, ignoreOverlap: true,
        startMinutesOfDay: a.from.startsWith(dateString) ? parseTimeStringToMinutes(a.from.substring(11, 16)) : 0,
        endMinutesOfDay: a.to.startsWith(dateString) ? parseTimeStringToMinutes(a.to.substring(11, 16)) : 24 * 60,
        ...freeSlotExtras,
    }));

    const absentSlots = absencesWithOriginals.map<CalendarSchedule>(({ expanded: absence, original }) => ({
        id: Guid.newGuid(), columnName, color: '#888888', opacity: 1, ignoreOverlap: true,
        startMinutesOfDay: absence.from.startsWith(dateString) ? parseTimeStringToMinutes(absence.from.substring(11, 16)) : 0,
        endMinutesOfDay: absence.to.startsWith(dateString) ? parseTimeStringToMinutes(absence.to.substring(11, 16)) : 24 * 60,
        content: absence.description?.length ? absence.description : absenceLabel,
        toolTipContent: absence.description?.length ? absence.description : absenceLabel,
        ...(onAbsenceClick ? { onClick: () => onAbsenceClick(original) } : {}),
    }));
    return [...freeSlots, ...freeSlotsExtra, ...absentSlots];
};
